'use client';
/**
 * OfflineProvider
 * 
 * Initializes the offline database and background sync on app mount.
 * Wraps children and provides the OfflineIndicator UI.
 */


import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { getOfflineDB } from '@/lib/offline/db';
import { startBackgroundSync, stopBackgroundSync } from '@/lib/offline/sync';
import { registerServiceWorker } from '@/lib/offline/register-sw';
import { autoPurgeStaleData, clearLeftoversOfEndedSession, followCleaningOfOtherTabs } from '@/lib/offline/clear-offline-data';
import { OfflineIndicator } from '@/components/molecules/OfflineIndicator';

interface OfflineProviderProps {
  children: React.ReactNode;
}

export function OfflineProvider({ children }: OfflineProviderProps) {
  const initialized = useRef(false);
  const stopFollowing = useRef<() => void>(() => undefined);
  // The offline engine is the clinic staff's. A patient on the portal has no use for a local database, and starting one
  // downloads a WASM module, registers a service worker and warms its cache with staff pages that the guard answers with
  // the login page. So it is not started there; if the same tab later moves on to a staff page, it starts then, once.
  const onPortal = usePathname().startsWith('/portal');

  useEffect(() => {
    if (onPortal || initialized.current) return;
    initialized.current = true;

    async function init() {
      try {
        // The login page is where every browser without a session ends up. There is no session on it, so nothing to sync or prefetch (the
        // guard would answer with the login page, which the service worker would keep as /reception); but it is the place to delete what an
        // ended session left behind - the log-out cleaning cut short, the cookie expired - on the server's word only, sparing the writes
        // that were never sent when the offline database can be opened here.
        if (window.location.pathname.startsWith('/login')) {
          const { outcome, keptWrites } = await clearLeftoversOfEndedSession();
          console.log('[Offline] Leftovers of an ended session:', outcome, keptWrites ? `(${keptWrites} unsent writes kept)` : '');
          registerServiceWorker();
          return;
        }

        // When another tab signs out (or cleans what a session left), this one lets go of the offline database: the file is shared, and a
        // file held open here cannot be deleted there.
        stopFollowing.current = followCleaningOfOtherTabs();

        // 1. Initialize SQLite WASM database
        await getOfflineDB();
        console.log('[Offline] Database initialized');

        // 2. Register Service Worker
        registerServiceWorker();

        // 3. Prefetch all main pages for offline use
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.ready.then((registration) => {
            registration.active?.postMessage({
              type: 'PREFETCH_PAGES',
              urls: [
                '/reception',
                '/examination', 
                '/lab',
                '/prescriptions',
                '/pos',
                '/inventory',
                '/services',
                '/orders',
                '/invoices',
                '/compensation',
                '/settings',
                '/users',
              ],
            });
          });
        }

        // 4. Start background sync
        startBackgroundSync();

        // 5. Auto-purge stale cache data (24h without sync)
        await autoPurgeStaleData();
      } catch (error) {
        console.error('[Offline] Initialization failed:', error);
        // App continues to work online-only — no crash
      }
    }

    init();
  }, [onPortal]);

  // On unmount only, as before: moving from the portal to a staff page, or the other way, must not stop an engine that is running.
  useEffect(() => () => {
    stopBackgroundSync();
    stopFollowing.current();
  }, []);

  return (
    <>
      {children}
      {/* Its poll of the pending writes opens the offline database by itself, so it stays off the portal too. */}
      {!onPortal && <OfflineIndicator />}
    </>
  );
}
